import { Link } from "react-router-dom";
import { type HatoVisibleColumns, resolveHatoColumns } from "../hatoColumns";
import { LIFECYCLE_GROUP, type LifecycleGroup, lifecycleLabel } from "../lifecycleStates";
import type { AnimalListItem, AnimalSex, LifecycleState } from "../types";
import { ProducingSubStateBadge } from "./ProducingSubStateBadge";

type Props = {
  animals: AnimalListItem[];
  // columnas-hato: visibilidad por columna (tag y estado son fijas). Sin la
  // prop, todas visibles — mantiene compatible el uso desde otros contextos.
  columns?: HatoVisibleColumns;
};

export function AnimalsList({ animals, columns }: Props) {
  const cols = columns ?? resolveHatoColumns(undefined);
  const headerClass =
    "px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]";
  return (
    <>
      {/* Mobile: lista de cards. Las preferencias de columnas aplican a los
          campos equivalentes de la card (spec columnas-hato §8): Don Álvaro no
          usa el nombre y no debe verlo tampoco en el celular. */}
      <div className="space-y-2 md:hidden">
        {animals.map((animal) => (
          <Link
            key={animal.id}
            to={`/app/hato/${animal.id}`}
            className="flex items-center gap-3 rounded-2xl border border-app-border bg-app-surface px-4 py-3 transition-colors hover:bg-app-surface-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-medium font-mono text-[14px] text-app-ink-1">
                  {animal.tag}
                </span>
                {cols.sex && <SexIcon sex={animal.sex} />}
                {cols.name && animal.name && (
                  <span className="truncate text-[13px] text-app-ink-2">{animal.name}</span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <LifecycleBadge state={animal.lifecycleState} sex={animal.sex} />
                <ProducingSubStateBadge animal={animal} />
                {/* En la card solo tiene señal con ≥1 — "0 lactancias" en cada
                    ternera sería ruido; la tabla desktop sí muestra el 0. */}
                {cols.parities && animal.parities != null && animal.parities > 0 && (
                  <span className="text-[11px] text-app-ink-3">
                    {animal.parities} {animal.parities === 1 ? "lactancia" : "lactancias"}
                  </span>
                )}
                {cols.lot && animal.currentLotName && (
                  <span className="text-[11px] text-app-ink-3">{animal.currentLotName}</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Desktop: tabla */}
      <div className="hidden overflow-hidden rounded-2xl border border-app-border bg-app-surface md:block">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-app-border border-b">
              <th className={headerClass}>Tag</th>
              {cols.name && <th className={headerClass}>Nombre</th>}
              {cols.sex && <th className={headerClass}>Sexo</th>}
              <th className={headerClass}>Estado</th>
              {cols.lot && <th className={headerClass}>Lote</th>}
              {cols.parities && <th className={headerClass}>Nº lact.</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-app-border">
            {animals.map((animal) => (
              <tr
                key={animal.id}
                className="cursor-pointer transition-colors hover:bg-app-surface-2"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/app/hato/${animal.id}`}
                    className="block font-medium font-mono text-app-ink-1 hover:text-app-emerald"
                  >
                    {animal.tag}
                  </Link>
                </td>
                {cols.name && (
                  <td className="px-4 py-3 text-app-ink-2">
                    <Link to={`/app/hato/${animal.id}`} className="block">
                      {animal.name ?? <span className="text-app-ink-3">—</span>}
                    </Link>
                  </td>
                )}
                {cols.sex && (
                  <td className="px-4 py-3">
                    <Link to={`/app/hato/${animal.id}`} className="block">
                      <SexIcon sex={animal.sex} />
                    </Link>
                  </td>
                )}
                <td className="px-4 py-3">
                  <Link
                    to={`/app/hato/${animal.id}`}
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    <LifecycleBadge state={animal.lifecycleState} sex={animal.sex} />
                    <ProducingSubStateBadge animal={animal} />
                  </Link>
                </td>
                {cols.lot && (
                  <td className="px-4 py-3 text-app-ink-2">
                    <Link to={`/app/hato/${animal.id}`} className="block">
                      {animal.currentLotName ?? <span className="text-app-ink-3">—</span>}
                    </Link>
                  </td>
                )}
                {cols.parities && (
                  <td className="px-4 py-3 text-app-ink-2">
                    <Link to={`/app/hato/${animal.id}`} className="block">
                      {animal.parities ?? <span className="text-app-ink-3">—</span>}
                    </Link>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SexIcon({ sex }: { sex: AnimalSex }) {
  if (sex === "female") {
    return (
      <span aria-label="Hembra" className="shrink-0 font-medium text-[12px] text-violet-500">
        ♀
      </span>
    );
  }
  return (
    <span aria-label="Macho" className="shrink-0 font-medium text-[12px] text-blue-500">
      ♂
    </span>
  );
}

const GROUP_CLASSES: Record<LifecycleGroup, string> = {
  cria: "bg-blue-100 text-blue-700",
  desarrollo: "bg-violet-100 text-violet-700",
  productiva: "bg-app-emerald-bg text-app-emerald",
  retirada: "bg-neutral-100 text-neutral-500",
};

function LifecycleBadge({ state, sex }: { state: LifecycleState; sex: AnimalSex }) {
  const group = LIFECYCLE_GROUP[state];
  const classes = GROUP_CLASSES[group];
  return (
    <span className={`inline-block rounded-xl px-2 py-0.5 font-medium text-[11px] ${classes}`}>
      {lifecycleLabel(state, sex)}
    </span>
  );
}
